import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Package, Plus, Trash2, AlertTriangle, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const CAP_LABELS = {
  catering:       "Catering & Craft",
  transport:      "Vanity Vans & Logistics",
  utilities:      "Set Comfort & Utilities",
  hardware_paint: "Hardware, Paint & Finishing",
  props:          "Props (Furniture / Decoration)",
  consumables:    "Consumables · Production Supply",
};

const PROP_SUBTYPES = [
  { key: "furniture",  label: "Furniture" },
  { key: "decoration", label: "Decoration" },
];

/** Supplies Vendor · SKU catalog with quantity-on-hand + low-stock alerts. */
export default function VendorSuppliesPanel({ org }) {
  const [items, setItems]   = useState([]);
  const [lowCount, setLowCount] = useState(0);
  const [addOpen, setAddOpen] = useState(false);

  const refresh = async () => {
    const { data } = await api.get(`/orgs/${org.id}/vendor-stock`);
    setItems(data.items || []);
    setLowCount(data.low_stock || 0);
  };
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  const adjust = async (sku, delta) => {
    try {
      await api.patch(`/orgs/${org.id}/vendor-stock/${sku.id}`, { delta });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Adjust failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this SKU from your catalog?")) return;
    try {
      await api.delete(`/orgs/${org.id}/vendor-stock/${id}`);
      toast.success("Removed");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <section className="space-y-8" data-testid="vendor-supplies-panel">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55 flex items-center gap-2">
            <Package size={11} className="text-[#D9383A]" /> Supplies Track · Quantity counter
          </div>
          <h2 className="font-display text-2xl uppercase tracking-tight mt-1">Stock Ledger</h2>
          <div className="mt-1 text-xs text-white/50 flex items-center gap-3">
            <span>{items.length} SKUs</span>
            {lowCount > 0 && (
              <span className="text-amber-300 flex items-center gap-1"
                    data-testid="low-stock-warning">
                <AlertTriangle size={11} /> {lowCount} low-stock
              </span>
            )}
          </div>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] btn-themed"
              data-testid="add-sku-btn">
              <Plus size={12} className="mr-1.5" /> Add SKU
            </Button>
          </DialogTrigger>
          <AddSkuDialog org={org} onClose={() => setAddOpen(false)} onAdded={refresh} />
        </Dialog>
      </div>

      {/* SKU list */}
      <div className="cs-card rounded-2xl overflow-hidden">
        <div className="grid grid-cols-[1.4fr_100px_130px_150px_80px] gap-3 px-5 py-3 border-b border-white/10 text-[10px] tracking-[0.22em] uppercase text-white/45">
          <div>Item</div><div>Unit</div><div>Capability</div><div className="text-center">Quantity</div><div />
        </div>
        {items.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-white/50">
            No SKUs yet — add your first item to start tracking.
          </div>
        ) : items.map((it) => (
          <div key={it.id}
               className={`grid grid-cols-[1.4fr_100px_130px_150px_80px] gap-3 px-5 py-3 items-center border-b border-white/5 hover:bg-white/[0.02]
                 ${it.is_low ? "bg-amber-500/[0.03]" : ""}`}
               data-testid={`sku-${it.id}`}>
            <div>
              <div className="text-sm text-white flex items-center gap-2">
                {it.name}
                {it.is_low && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] tracking-[0.18em] uppercase bg-amber-500/20 text-amber-200"
                        data-testid={`sku-low-${it.id}`}>
                    Low
                  </span>
                )}
              </div>
              {it.unit_price > 0 && (
                <div className="text-[10px] text-white/45 mt-0.5">₹{Number(it.unit_price).toLocaleString()}/{it.unit}</div>
              )}
            </div>
            <div className="text-xs text-white/70">{it.unit}</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-white/70">
              {CAP_LABELS[it.capability] || it.capability}
              {it.capability === "props" && it.props_subtype && (
                <span className="ml-1.5 text-white/50">· {it.props_subtype}</span>
              )}
            </div>
            <div className="flex items-center justify-center gap-1.5">
              <Button size="sm" variant="ghost" onClick={() => adjust(it, -1)}
                      className="h-7 w-7 p-0 hover:bg-red-500/10 hover:text-red-300 text-white/60"
                      data-testid={`sku-minus-${it.id}`}>
                <TrendingDown size={12} />
              </Button>
              <div className="min-w-[38px] text-center font-mono text-sm text-white">{it.quantity_on_hand}</div>
              <Button size="sm" variant="ghost" onClick={() => adjust(it, 1)}
                      className="h-7 w-7 p-0 hover:bg-emerald-500/10 hover:text-emerald-300 text-white/60"
                      data-testid={`sku-plus-${it.id}`}>
                <TrendingUp size={12} />
              </Button>
            </div>
            <div className="text-right">
              <Button size="sm" variant="ghost" onClick={() => remove(it.id)}
                      className="h-8 w-8 p-0 text-white/45 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`sku-delete-${it.id}`}>
                <Trash2 size={13} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AddSkuDialog({ org, onClose, onAdded }) {
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("piece");
  const [qty, setQty]   = useState("0");
  const [reorder, setReorder] = useState("0");
  const [price, setPrice]     = useState("");
  const [cap, setCap]         = useState((org.vendor_capabilities || [])[0] || "");
  const [subtype, setSubtype] = useState("furniture");
  const [busy, setBusy]       = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        unit: unit.trim(),
        quantity_on_hand: parseInt(qty, 10) || 0,
        reorder_at: parseInt(reorder, 10) || 0,
        unit_price: Number(price) || 0,
        capability: cap,
      };
      if (cap === "props") payload.props_subtype = subtype;
      await api.post(`/orgs/${org.id}/vendor-stock`, payload);
      toast.success("SKU added");
      onAdded?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="bg-black border-white/15 text-white" data-testid="add-sku-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl uppercase tracking-tight">Add SKU</DialogTitle>
        <DialogDescription className="text-white/55">
          Set a reorder threshold so we can flag low stock automatically.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        <div>
          <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Item name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. Marine Plywood 8×4 · 12mm"
                 className="mt-1.5 bg-black/30 border-white/15"
                 data-testid="sku-name-input" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Unit</Label>
            <Input value={unit} onChange={(e) => setUnit(e.target.value)}
                   placeholder="piece / kg / m"
                   className="mt-1.5 bg-black/30 border-white/15"
                   data-testid="sku-unit-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">On hand</Label>
            <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)}
                   className="mt-1.5 bg-black/30 border-white/15 font-mono"
                   data-testid="sku-qty-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Reorder at</Label>
            <Input type="number" value={reorder} onChange={(e) => setReorder(e.target.value)}
                   className="mt-1.5 bg-black/30 border-white/15 font-mono"
                   data-testid="sku-reorder-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Unit price (₹)</Label>
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)}
                   placeholder="Optional"
                   className="mt-1.5 bg-black/30 border-white/15"
                   data-testid="sku-price-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Capability</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {(org.vendor_capabilities || []).map((c) => (
                <button key={c} onClick={() => setCap(c)}
                        data-testid={`sku-cap-${c}`}
                        className={`px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.2em] border
                          ${cap === c
                            ? "border-[#D9383A]/60 bg-[#D9383A]/15 text-[#D9383A]"
                            : "border-white/15 text-white/55 hover:border-white/35"}`}>
                  {CAP_LABELS[c] || c}
                </button>
              ))}
            </div>
          </div>
        </div>

        {cap === "props" && (
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Prop sub-type</Label>
            <div className="mt-1.5 flex gap-2">
              {PROP_SUBTYPES.map((s) => (
                <button key={s.key} onClick={() => setSubtype(s.key)}
                        data-testid={`sku-subtype-${s.key}`}
                        className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] border
                          ${subtype === s.key
                            ? "border-[#F5B82E]/60 bg-[#F5B82E]/15 text-[#F5B82E]"
                            : "border-white/15 text-white/55 hover:border-white/35"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end pt-4">
        <Button onClick={submit} disabled={!name || !cap || busy}
                className="h-10 px-5 rounded-full text-[10px] uppercase tracking-[0.22em] btn-themed"
                data-testid="sku-submit">
          {busy ? "Adding…" : "Add SKU"}
        </Button>
      </div>
    </DialogContent>
  );
}
